import { useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { C } from "../../shared/theme";
import { CompassRow } from "../compass/CompassParts";
import { ListRow } from "../../shared/ListRow";
import { COMPASS_KINDS } from "../compass/compassKinds";
import { categoryGroups } from "../compass/compassLists";
import { CategoryItemEditor } from "../compass/CategoryItemEditor";
import { PlanItemEditor } from "../dreams/PlanItemEditor";
import { StatementEditor } from "../statements/StatementEditor";
import { statementsInOrder, statementText } from "../statements/statementKinds";
import { useTaskLinkLists } from "../tasks/TaskLinksContext";
import { TASK_LINKS } from "../tasks/taskLinks";
import { LinkedTasks } from "../tasks/LinkedTasks";
import { RadarGroup, CappedList } from "./RadarGroup";

// Radar's Habits, Dreams and Fears (Craig, 25 Oct 2026: "add a Dreams section (show 5 with no images - just the text -
// and allow the user to 'show more')", the same for Fears, and "add a Habits section"). Each lists its items in the
// order its own page does, as that page's rows, five before "Show more"; tapping one opens it to change, here. A dream or
// fear also shows the tasks for it (see WithTasks); `linkTasks` is { tasks, row(task, field), onAddTask(link, id) }. Dreams and
// fears come from NudgeApp's listeners (TaskLinksContext).
// `group` is the RadarGroup's fold, drag and heading props. A group sits in a draggable wrapper, which would trap an
// editor drawn inside it, so editors go into `editorHost`, an element at the top of the page.

const HABITS = COMPASS_KINDS.habits;

// A list that couldn't load says so where its group would be.
const LoadError = ({ what }) => (
  <p role="alert" style={{ margin: "0 0 18px", fontSize: 15, lineHeight: 1.5, color: C.red }}>
    We couldn't load your {what}. Check your connection, then open Radar again.
  </p>
);

// Every habit, category by category, in each category's order.
export function RadarHabits({ group, compass, setCompass, goals, editorHost }) {
  const categories = useMemo(() => compass?.categories || [], [compass]);
  const habits = useMemo(() => categoryGroups(categories, HABITS.field)
    .flatMap(({ category, items }) => items.map(it => ({ ...it, categoryId: category.id }))), [categories]);
  const [editing, setEditing] = useState(null); // { categoryId, index } of the habit open to change
  return (
    <>
      <RadarGroup {...group} count={habits.length} layout="card">
        <CappedList items={habits} renderItem={({ item, text, categoryId, index }, i) => (
          <CompassRow key={`${categoryId}-${index}`} habit={item} text={text} first={i === 0} editLabel="Edit habit"
            onEdit={() => setEditing({ categoryId, index })} />
        )} />
      </RadarGroup>
      {editing && editorHost && ReactDOM.createPortal(
        <CategoryItemEditor kind={HABITS} categories={categories} goals={goals} from={editing} setCompass={setCompass}
          onClose={() => setEditing(null)} />,
        editorHost
      )}
    </>
  );
}

// A dream or fear as its page's row, with its tasks under it (LinkedTasks). The fold tucks into the row's bottom padding.
function WithTasks({ link, id, text, first, editLabel, onEdit, linkTasks }) {
  return (
    <div>
      <ListRow text={text} first={first} editLabel={editLabel} onEdit={onEdit} />
      <LinkedTasks link={link} id={id} name={text} linkTasks={linkTasks} style={{ marginTop: -10 }} />
    </div>
  );
}

// Every dream's title, in the Dreamboard's order, without its photo, each with its tasks.
export function RadarDreams({ group, editorHost, linkTasks }) {
  const { items, error, update, remove } = useTaskLinkLists().dreams;
  const [editingId, setEditingId] = useState(null);
  const editing = items.find(d => d.id === editingId) || null; // as it is now; it closes if deleted elsewhere
  if (error) return <LoadError what="dreams" />;
  return (
    <>
      <RadarGroup {...group} count={items.length} layout="card">
        <CappedList items={items} renderItem={(dream, i) => (
          <WithTasks key={dream.id} link={TASK_LINKS.dreams} id={dream.id} text={dream.title} first={i === 0} editLabel="Edit dream"
            onEdit={() => setEditingId(dream.id)} linkTasks={linkTasks} />
        )} />
      </RadarGroup>
      {editing && editorHost && ReactDOM.createPortal(
        <PlanItemEditor kind="dreams" item={editing} onSave={(fields) => update(editing.id, fields)}
          onDelete={() => remove(editing.id)} onClose={() => setEditingId(null)} />,
        editorHost
      )}
    </>
  );
}

// Every fear, category by category in the user's category order, each in its place in its category. (The Fears page's
// dragged group order lasts only that session, so Radar keeps the categories' own order.)
export function RadarFears({ group, goals, compass, editorHost, linkTasks }) {
  const categories = useMemo(() => compass?.categories || [], [compass]);
  const { items, error, update, remove } = useTaskLinkLists().fears;
  const fears = useMemo(() => statementsInOrder("fears", items, categories), [items, categories]);
  const [editingId, setEditingId] = useState(null);
  const editing = items.find(f => f.id === editingId) || null;
  if (error) return <LoadError what="fears" />;
  return (
    <>
      <RadarGroup {...group} count={fears.length} layout="card">
        <CappedList items={fears} renderItem={(fear, i) => (
          <WithTasks key={fear.id} link={TASK_LINKS.fears} id={fear.id} text={statementText("fears", fear)} first={i === 0} editLabel="Edit fear"
            onEdit={() => setEditingId(fear.id)} linkTasks={linkTasks} />
        )} />
      </RadarGroup>
      {editing && editorHost && ReactDOM.createPortal(
        <StatementEditor kind="fears" statement={editing} goals={goals} categories={categories}
          onSave={(fields) => update(editing, fields)} onDelete={() => remove(editing)} onClose={() => setEditingId(null)} />,
        editorHost
      )}
    </>
  );
}
